import React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import MedicationRemindersPanel from '../../features/medicationReminders/MedicationRemindersPanel';
import { fetchMedications } from '../../services/medicationService';
import { useRole } from '../../contexts/RoleContext';
import { canManageMedicationDefinitions } from '../../components/Dashboard/shared/medicationPermissions';
import { CARE_PROFILE_ROUTE } from '../../constants/profileRoutes';

const MedicationRemindersPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { childrenWithAccess = [], getUserRoleForChild, loading: roleLoading } = useRole();
  const child = childrenWithAccess.find((item) => item.id === childId) || null;
  const [medications, setMedications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (roleLoading || !child) return undefined;
    let active = true;
    fetchMedications(childId, true).then((items) => {
      if (active) { setMedications(items); setLoading(false); }
    }).catch((nextError) => {
      if (active) { setError(nextError?.message || 'Medication reminders could not be loaded.'); setLoading(false); }
    });
    return () => { active = false; };
  }, [child, childId, roleLoading]);

  const focusedMedicationId = new URLSearchParams(location.search).get('medicationId') || '';
  const queryReturnTo = new URLSearchParams(location.search).get('returnTo') || '';
  const safeQueryReturnTo = queryReturnTo.startsWith('/') && !queryReturnTo.startsWith('//')
    ? queryReturnTo
    : '';
  const returnToMedications = () => navigate(location.state?.returnTo || safeQueryReturnTo || CARE_PROFILE_ROUTE, {
    state: location.state?.returnState || { childId, openChildMedicationManager: true },
  });

  if (roleLoading || (loading && child)) return <Box sx={{ minHeight: '70dvh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!roleLoading && !child) return <Navigate to="/dashboard" replace />;

  const userRole = getUserRoleForChild?.(childId) || '';
  const canManage = canManageMedicationDefinitions({ child, userRole });

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#F8FAF9', pb: 4 }}>
      <Box sx={{ maxWidth: 680, mx: 'auto', bgcolor: 'background.paper', minHeight: '100dvh' }}>
        <Stack sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button onClick={returnToMedications} startIcon={<ArrowBackRoundedIcon />} aria-label="Back to medications" sx={{ alignSelf: 'flex-start', px: 0, textTransform: 'none', fontWeight: 800 }}>Back</Button>
          <Typography variant="overline" color="text.secondary" sx={{ mt: 0.5, fontWeight: 900, letterSpacing: '0.12em' }}>{child.name || 'Care profile'}'s care circle</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>Reminders</Typography>
        </Stack>
        {error ? <Alert severity="warning" sx={{ m: 2 }}>{error}</Alert> : null}
        {!error ? <MedicationRemindersPanel childId={childId} childName={child.name || ''} medications={medications} canManage={canManage} pageLayout focusedMedicationId={focusedMedicationId} onReminderSaved={returnToMedications} /> : null}
      </Box>
    </Box>
  );
};

export default MedicationRemindersPage;
